import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, ShieldCheck, Eye, Clock, ExternalLink } from "lucide-react";
import { API } from "../data/site";
import { useSettings } from "../hooks/useCms";
import PageHeader from "../components/PageHeader";
import { FadeUp } from "../components/motion";
import CyclingImage from "../components/CyclingImage";
import SEO from "../components/SEO";

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#1A1A1A]/25 focus:border-[#8C6A4F] focus:ring-0 outline-none py-4 text-base placeholder:text-[#A3A09B] transition-colors duration-300 rounded-none";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="eyebrow text-[#5C5A56] block mb-1">{label}</span>
    {children}
  </label>
);

const RatingPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-2 py-3" data-testid="input-rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? null : n)}
        aria-label={`Rate ${n} out of 5`}
        aria-pressed={value >= n}
        data-testid={`rating-${n}`}
        className="group p-1.5"
      >
        <span
          className={`block w-3 h-3 rotate-45 transition-colors duration-300 ${
            value >= n ? "bg-[#8C6A4F]" : "bg-[#1A1A1A]/15 group-hover:bg-[#8C6A4F]/50"
          }`}
        />
      </button>
    ))}
    <span className="ml-3 font-mono text-[0.62rem] tracking-[0.18em] uppercase text-[#A3A09B]" data-testid="rating-hint">
      {value ? `${value} / 5` : "Optional"}
    </span>
  </div>
);

const Testimonial = () => {
  const [params] = useSearchParams();
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const settings = useSettings();
  const [form, setForm] = useState({
    name: params.get("client") || "",
    email: "",
    project: params.get("project") || "",
    rating: null,
    text: "",
    permission: false,
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.permission) {
      toast.error("Please allow us to display your review before submitting.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/testimonials/submit`, {
        name: form.name,
        email: form.email,
        project: form.project,
        rating: form.rating,
        text: form.text,
        permission_granted: form.permission,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <SEO
        title="Share Your Experience | On Point Homes"
        description="Share your experience building with On Point Homes — your story helps future Idaho homeowners build with confidence."
        noindex
      />
      <PageHeader
        testid="testimonial-header"
        eyebrow="Client Stories"
        lines={[{ text: "Share Your" }, { text: "Experience", italic: true }]}
        description="Building a home is personal — and so is your story. A few honest words about your experience help future homeowners take the first step with confidence."
      />

      <section className="pb-28 md:pb-40">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {done ? (
            <FadeUp className="lg:col-span-7" y={20}>
              <div className="border border-[rgba(26,26,26,0.15)] bg-white/60 p-10 md:p-14" data-testid="testimonial-success">
                <p className="eyebrow text-[#8C6A4F] mb-6">Thank You</p>
                <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight">
                  Your story means the world to us.
                </h2>
                <p className="mt-6 text-base md:text-lg text-[#5C5A56] leading-relaxed max-w-xl">
                  We read every word personally. Your review will appear on our website once we've had a chance to
                  look it over — usually within a day or two.
                </p>
                {settings.google_review_url && (
                  <div className="mt-10 pt-10 border-t border-[rgba(26,26,26,0.12)]" data-testid="google-review-prompt">
                    <p className="eyebrow text-[#8C6A4F] mb-4">One More Small Favor</p>
                    <p className="text-base text-[#5C5A56] leading-relaxed max-w-xl">
                      If you have another moment, sharing a few words on Google helps other Idaho families find a
                      builder they can trust.
                    </p>
                    <a
                      href={settings.google_review_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="google-review-link"
                      className="mt-6 inline-flex items-center gap-3 border border-[#1A1A1A] px-8 py-4 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-300"
                    >
                      Leave a Google Review <ExternalLink size={14} />
                    </a>
                  </div>
                )}
                <Link
                  to="/"
                  data-testid="testimonial-back-home"
                  className="mt-10 inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit"
                >
                  <ArrowLeft size={14} /> Back to Home
                </Link>
              </div>
            </FadeUp>
          ) : (
            <FadeUp className="lg:col-span-7">
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" data-testid="testimonial-form">
                <Field label="Your Name *" testid="field-name">
                  <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="input-name" />
                </Field>
                <Field label="Email *" testid="field-email">
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="input-email" />
                </Field>
                <Field label="Your Home / Project" testid="field-project">
                  <input value={form.project} onChange={set("project")} className={inputCls} placeholder="e.g. The Willowbrook, Eagle" data-testid="input-project" />
                </Field>
                <div data-testid="field-rating">
                  <span className="eyebrow text-[#5C5A56] block mb-1">Your Rating</span>
                  <RatingPicker value={form.rating} onChange={(r) => setForm({ ...form, rating: r })} />
                </div>
                <div className="md:col-span-2">
                  <Field label="Your Experience *" testid="field-text">
                    <textarea
                      required
                      rows={6}
                      value={form.text}
                      onChange={set("text")}
                      className={`${inputCls} resize-none`}
                      placeholder="What was it like building with us? What surprised you, what did you love, what should future homeowners know?"
                      data-testid="input-text"
                    />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="field-permission">
                    <input
                      type="checkbox"
                      required
                      checked={form.permission}
                      onChange={set("permission")}
                      className="mt-1 w-4 h-4 accent-[#8C6A4F] shrink-0"
                      data-testid="input-permission"
                    />
                    <span className="text-sm text-[#5C5A56] leading-relaxed">
                      I allow On Point Homes to display my review, first name, and project on their website.
                      My email address is never published.
                    </span>
                  </label>
                </div>
                <div className="md:col-span-2 pt-4">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="testimonial-submit-btn"
                    className="btn-sweep bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-10 py-5 inline-flex items-center gap-3 disabled:opacity-50"
                  >
                    {sending ? "Sending…" : "Share Your Story"} <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            </FadeUp>
          )}

          <FadeUp className="lg:col-span-5" delay={0.15}>
            <div className="lg:sticky lg:top-28 flex flex-col gap-10">
              <div className="img-zoom relative aspect-[4/3]">
                <CyclingImage start={24} interval={5800} alt="A finished custom home by On Point Homes in Idaho" />
              </div>
              <div className="border border-[rgba(26,26,26,0.15)] p-8">
                <p className="eyebrow text-[#8C6A4F] mb-6">Good To Know</p>
                <div className="flex flex-col gap-5 text-sm text-[#5C5A56]">
                  <div className="flex items-start gap-3">
                    <ShieldCheck size={15} className="text-[#8C6A4F] mt-0.5 shrink-0" />
                    <span>Your email stays private — it's only used so we can thank you, never shown on the site.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={15} className="text-[#8C6A4F] mt-0.5 shrink-0" />
                    <span>Reviews are published after a quick personal review — usually within a day or two.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Eye size={15} className="text-[#8C6A4F] mt-0.5 shrink-0" />
                    <span>Honest words only. We never edit the meaning of what you write.</span>
                  </div>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
};

export default Testimonial;
